/*
 * consent/catalog.ts — fonte única do que o site guarda no navegador.
 *
 * O painel de preferências e a seção 11 da Política de Privacidade leem esta
 * mesma lista; um item novo de armazenamento entra aqui, ou não existe para a
 * pessoa. As preferências que ela mesma define (tema, barra lateral, widget) são
 * NECESSÁRIAS: guardam uma ação explícita dela, a isenção que ANPD e CNIL
 * reconhecem.
 */
import { SITE_NAME } from '@/lib/seo';
import { COOKIE_NOTICE_KEY } from './consent';

export type StorageKind = 'cookie' | 'localStorage' | 'sessionStorage';

export interface StorageItem {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
  kind: StorageKind;
}

/** Uma categoria do catálogo. Só há necessários: sempre ativos, sem interruptor. */
export interface ConsentCategoryInfo {
  id: 'necessary';
  title: string;
  description: string;
  items: readonly StorageItem[];
}

export const STORAGE_KIND_LABEL: Record<StorageKind, string> = {
  cookie: 'Cookie',
  localStorage: 'Armazenamento local',
  sessionStorage: 'Armazenamento da sessão',
};

const FIRST_PARTY = SITE_NAME;

export const CONSENT_CATALOG: readonly ConsentCategoryInfo[] = [
  {
    id: 'necessary',
    title: 'Necessários',
    description:
      'Mantêm a sessão, a segurança e as preferências que você mesmo escolhe. Sem eles o site não funciona, por isso ficam sempre ativos.',
    items: [
      {
        name: 'CognitoIdentityServiceProvider.*',
        provider: FIRST_PARTY,
        purpose: 'Mantém a sua sessão no console depois de entrar.',
        duration: 'Até encerrar a sessão',
        kind: 'localStorage',
      },
      {
        name: 'habilita.theme',
        provider: FIRST_PARTY,
        purpose: 'Lembra o tema escolhido (claro, escuro ou do sistema).',
        duration: 'Até você mudar',
        kind: 'localStorage',
      },
      {
        name: 'habilita.nav-collapsed',
        provider: FIRST_PARTY,
        purpose: 'Lembra se a barra lateral do console fica recolhida.',
        duration: 'Até você mudar',
        kind: 'localStorage',
      },
      {
        name: 'habilita.shiba-open',
        provider: FIRST_PARTY,
        purpose: 'Lembra se o assistente fica aberto.',
        duration: 'Até você mudar',
        kind: 'localStorage',
      },
      {
        name: 'habilita.shiba-conversation',
        provider: FIRST_PARTY,
        purpose: 'Continua a conversa do assistente entre as páginas.',
        duration: 'Até fechar a aba',
        kind: 'sessionStorage',
      },
      {
        name: 'habilita.shiba-hidden',
        provider: FIRST_PARTY,
        purpose: 'Lembra que você ocultou o assistente nesta visita.',
        duration: 'Até fechar a aba',
        kind: 'sessionStorage',
      },
      {
        name: 'habilita.site-chat',
        provider: FIRST_PARTY,
        purpose: 'Continua a conversa com a Shiba do site entre as páginas desta visita.',
        duration: 'Até fechar a aba',
        kind: 'sessionStorage',
      },
      {
        name: COOKIE_NOTICE_KEY,
        provider: FIRST_PARTY,
        purpose: 'Lembra que você já leu o aviso de cookies.',
        duration: 'Até você limpar os dados do site',
        kind: 'localStorage',
      },
    ],
  },
];
